import { IpfError } from '@ipfabric/frontend-shared-assets/utils/IpfError';
import {
  type NonEmptyArray,
  PI_RADIANS_360,
  getOrientedAngularDistance,
  normalizeAngle,
} from '@ipfabric/utils';
import { isBetweenLeftInclusive } from '@ipfabric/utils/fp/number';
import { ___, partialApply } from '@ipfabric/utils/fp/partialApply';
import { isNonEmpty } from 'fp-ts/lib/Array';
import { pipe } from 'fp-ts/lib/function';
import { getFromCyclicArray } from 'utils/array/getFromCyclicArray';

/**
 * Take a list of elements that have some angle property (expected pre-sorted by angle value),
 * find groups of elements that have too similar angle (lower than desiredMinDistance)
 * and change these angles a bit to reduce overlaying elements
 *
 * This is useful e.g. for highlighting hovered edges - when two or more edges overlap each other,
 * it is impossible to hover over them except one.
 *
 * Current algorithm is quite simple:
 * - find slices in the list where angularDistance(firstElement, lastElement) < desiredMinDistance
 *   - let's call those overlap groups
 * - check angles of the two elements surrounding the overlap group
 *   - try spreading the overlap group evenly by desiredMinDistance between them
 *   - if not possible because gap between surrounding elements is too small, at least evenly distribute
 *     the group over the available gap
 *
 * This may not cover all possible cases well, but it is good enough for now to cover common cases where
 * there are just multiple elements in the exactly same direction.
 */
export const spreadCloseAngles =
  <T,>(
    desiredMinDistance: number,
    getAngle: (element: T) => number,
    setAngle: (element: T, angle: number) => T,
  ) =>
  // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: TBD cleanup eventually
  (sortedListOfElements: T[]): T[] => {
    const areElementsTooClose = (firstIndex: number, secondIndex: number) =>
      getOrientedAngularDistance(
        // Fixed noUncheckedIndexedAccess NIM-19408
        getAngle(result[firstIndex]!),
        getAngle(result[secondIndex]!),
      ) < desiredMinDistance;

    if (!isNonEmpty(sortedListOfElements)) {
      return sortedListOfElements;
    }
    const result = [...sortedListOfElements] as NonEmptyArray<T>;
    if (
      process.env.NODE_ENV === 'development' ||
      process.env.NODE_ENV === 'test'
    ) {
      for (let i = 1; i < result.length; i += 1) {
        // Fixed noUncheckedIndexedAccess NIM-19408
        if (getAngle(result[i - 1]!) > getAngle(result[i]!)) {
          throw new IpfError(
            'INVALID_ARGUMENT',
            'this function expects elements sorted by angle value as input',
          );
        }
      }
      if (
        !result.map(getAngle).every(isBetweenLeftInclusive(0, PI_RADIANS_360))
      ) {
        throw new IpfError(
          'INVALID_ARGUMENT',
          'this function expects elements with normalized angles',
        );
      }
    }
    for (
      let groupEnd = 1, endAt = result.length;
      groupEnd < endAt;
      groupEnd += 1
    ) {
      let groupStart = groupEnd - 1;
      if (groupStart === 0 && areElementsTooClose(result.length - 1, 0)) {
        // scan for group over zero angle = over list end/start
        groupStart = result.length;
        while (groupStart > 0 && areElementsTooClose(groupStart - 1, 0)) {
          groupStart -= 1;
        }
        endAt = groupStart;
      }

      // cover a group of elements that are all inside an angle of desiredMinDistance
      while (groupEnd < endAt && areElementsTooClose(groupStart, groupEnd)) {
        groupEnd += 1;
      }

      if (groupStart === groupEnd - 1) {
        // single element in group => no too close elements
        continue;
      }

      // now angles from j to i - 1 are in a range less than desiredMinDistance
      const singleGroup = groupEnd === groupStart; // all angles form a single too-close group
      // Fixed noUncheckedIndexedAccess NIM-19408
      const firstAngle = getAngle(result[groupStart]!);
      const lastAngle = getAngle(getFromCyclicArray(result, groupEnd - 1));

      const lastAngleForCenter =
        firstAngle <= lastAngle ? lastAngle : lastAngle + PI_RADIANS_360;
      const centerAngle = firstAngle + (lastAngleForCenter - firstAngle) / 2;

      // number of gaps = number of close angles + 1 because there are n - 1 gaps
      // between n elements, but we want to make gap from nearly surrounding elements
      // too -> n + 1 gaps around n elements.
      const numGaps =
        groupEnd + (groupStart < groupEnd ? 0 : result.length) - groupStart + 1;

      const desiredSpreadHalf = (desiredMinDistance * numGaps) / 2;

      // startSpreadAtAngle < endSpreadAtAngle and they may overflow [0, 2pi) to ease
      // calculation of the spread, we'll normalize to [0, 2pi) at the end
      const startSpreadAtAngle =
        centerAngle -
        Math.min(
          desiredSpreadHalf,
          singleGroup
            ? Number.POSITIVE_INFINITY
            : pipe(
                getFromCyclicArray(result, groupStart - 1),
                getAngle,
                partialApply(getOrientedAngularDistance, ___, centerAngle),
              ),
        );

      const endSpreadAtAngle =
        centerAngle +
        Math.min(
          desiredSpreadHalf,
          singleGroup
            ? Number.POSITIVE_INFINITY
            : pipe(
                getFromCyclicArray(result, groupEnd),
                getAngle,
                partialApply(getOrientedAngularDistance, centerAngle, ___),
              ),
        );

      // determine how big gaps can we make to not interfere with elements around the group being spreaded
      const actualGap = (endSpreadAtAngle - startSpreadAtAngle) / numGaps;

      // perform the spread
      for (
        let i = groupStart, gapNumber = 1;
        gapNumber < numGaps;
        i = (i + 1) % result.length, gapNumber += 1
      ) {
        result[i] = setAngle(
          // Fixed noUncheckedIndexedAccess NIM-19408
          result[i]!,
          normalizeAngle(startSpreadAtAngle + actualGap * gapNumber),
        );
      }
    }
    return result;
  };
